<template>
  <div
    v-clickOutside.dropdownOpen="{
      handler: 'closeDropdown',
      exclude: [],
    }"
    class="multi-select"
    @click="toggleDropdown"
  >
    <div class="selected-options">
      <span v-if="selected.length === 0" class="placeholder">Select...</span>
      <span v-for="option in selectedOptions" :key="option.value" class="selected-option">
        <TruncatedText :text="option.label" :maxlength="15" />
        <button type="button" class="remove-btn" @click.stop="removeOption(option.value)">x</button>
      </span>
    </div>
    <div v-show="dropdownOpen" class="dropdown" :class="dropdownPositionClass">
      <div
        v-for="option in normalizedOptions"
        :key="option.value"
        class="dropdown-option"
        :class="{ selected: isSelected(option.value) }"
        @click.stop="toggleOption(option.value)"
      >
        <input type="checkbox" :checked="isSelected(option.value)" @change="handleTouch(option.value)" />
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import clickOutside from '@/directives/clickOutside';
import { Component, Prop, Vue } from 'vue-property-decorator';
import TruncatedText from '@/components/TruncatedText.vue';

interface Option {
  value: string | number;
  label: string;
}

type OpenDirection = 'top' | 'down';

@Component({
  components: {
    TruncatedText,
  },
  directives: {
    clickOutside,
  },
})
export default class MultiSelect extends Vue {
  @Prop({ type: Array, default: () => [] }) readonly options!: Array<string | Option>;
  @Prop({ type: Array, default: () => [] }) readonly value!: Array<string | number>;
  @Prop({ type: String, default: 'down' }) readonly openDirection!: OpenDirection;

  dropdownOpen: boolean = false;
  selected: Array<string | number> = [];

  created() {
    this.selected = [...this.value];
  }

  get normalizedOptions(): Option[] {
    return this.options.map((option) => (typeof option === 'string' ? { value: option, label: option } : option));
  }

  get selectedOptions(): Option[] {
    return this.normalizedOptions.filter((option) => this.selected.includes(option.value));
  }

  get dropdownPositionClass(): string {
    return this.openDirection === 'top' ? 'dropdown-up' : 'dropdown-down';
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
  }

  toggleOption(val: string | number): void {
    const index = this.selected.indexOf(val);
    if (index === -1) {
      this.selected.push(val);
    } else {
      this.selected.splice(index, 1);
    }
    this.$emit('input', this.selected);
  }

  isSelected(val: string | number): boolean {
    return this.selected.includes(val);
  }

  removeOption(val: string | number): void {
    const index = this.selected.indexOf(val);
    if (index !== -1) {
      this.selected.splice(index, 1);
      this.$emit('input', this.selected);
    }
  }

  handleTouch(val: string | number): void {
    this.toggleOption(val);
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';

.multi-select {
  position: relative;
  width: 100%;
  border: 1px solid $multi-select-border-color;
  border-radius: 4px;
  padding: 6px;
  background-color: $multi-select-background;
  cursor: pointer;
  user-select: none;
}

.selected-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 30px;
  align-items: center;
}

.placeholder {
  color: $multi-select-placeholder-color;
}

.selected-option {
  border: 1px solid $multi-select-selected-option-border-color;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 14px;
  color: $multi-select-selected-option-text-color;
}

.remove-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 4px;
  color: $multi-select-remove-icon-color;
}

.dropdown {
  position: absolute;
  left: 0;
  right: 0;
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid $multi-select-dropdown-border-color;
  background-color: $multi-select-dropdown-background;
  z-index: 10;
}

.dropdown-down {
  top: 100%;
  bottom: auto;
  margin-top: 4px;
}

.dropdown-up {
  bottom: 100%;
  top: auto;
  margin-bottom: 4px;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  text-align: left;
  cursor: pointer;
  color: $multi-select-dropdown-option-text-color;
}
</style>
